import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Activity, BarChart3, Bot, Car, FileSpreadsheet, GitCompareArrows, LayoutDashboard, LogOut, Map, Radio, Route, Settings, ShieldCheck, Trophy, Upload, Users, Menu, Video, X, Building2 } from "lucide-react";
import dayjs from "dayjs";
import { useAuth } from "@/lib/auth";
import { LanguageSwitch, useT } from "@/lib/i18n";
import TenantSwitcher from "@/components/TenantSwitcher";
import { TZ, tzCity } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
  { to: "/map", label: "Live Map", icon: Map, id: "live-map" },
  { to: "/reconciliation", label: "Reconciliation", icon: GitCompareArrows, id: "reconciliation-inbox" },
  { to: "/journeys", label: "Journeys", icon: Route, id: "journeys" },
  { to: "/passengers", label: "Passengers", icon: Users, id: "passengers" },
  { to: "/cctv", label: "CCTV & Evidence", icon: Video, id: "cctv" },
  { to: "/uber", label: "Uber Import", icon: Upload, id: "uber-import" },
  { to: "/vehicles", label: "Vehicles", icon: Car, id: "vehicles" },
  { to: "/drivers", label: "Drivers & Shifts", icon: Users, id: "drivers" },
  { to: "/scorecard", label: "Driver Scorecard", icon: Trophy, id: "scorecard" },
  { to: "/health", label: "Device Health", icon: Radio, id: "device-health" },
  { to: "/reports", label: "Reports", icon: FileSpreadsheet, id: "reports" },
  { to: "/assistant", label: "AI Assistant", icon: Bot, id: "assistant" },
  { to: "/settings", label: "Settings", icon: Settings, id: "settings" },
  { to: "/tenants", label: "Fleets", icon: Building2, id: "tenants", superAdminOnly: true },
];

function Clock() {
  const [now, setNow] = useState(dayjs());
  useEffect(() => {
    const t = setInterval(() => setNow(dayjs()), 1000);
    return () => clearInterval(t);
  }, []);
  return <span data-testid="header-london-clock" className="font-mono text-sm text-slate-200">{now.tz(TZ).format("HH:mm:ss")} <span className="text-slate-500">{tzCity()}</span></span>;
}

export default function AppShell() {
  const { user, tenant, logout, can } = useAuth();
  const t = useT();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  if (!user) return null;
  const demo = !!tenant?.settings?.demoSeededAt;
  return (
    <div className="flex h-full min-h-screen">
      <aside className={cn("fixed inset-y-0 left-0 z-40 w-[260px] flex-col bg-[#0d111a] border-r border-[#26334d] transition-transform lg:static lg:translate-x-0 flex", open ? "translate-x-0" : "-translate-x-full")}>
        <div className="flex items-center gap-3 px-5 h-16 border-b border-[#26334d]">
          <div className="h-9 w-9 rounded-lg bg-blue-500/15 border border-blue-500/30 flex items-center justify-center"><ShieldCheck className="h-5 w-5 text-blue-400" /></div>
          <div>
            <div className="font-display font-semibold tracking-tight text-slate-100">Fleetlio</div>
            <div className="tg-label !text-[10px]">Fleet audit</div>
          </div>
          <button className="ml-auto lg:hidden text-slate-400" onClick={() => setOpen(false)} data-testid="sidebar-close-button"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-4 py-4 border-b border-[#26334d]">
          <div className="tg-label mb-1">{t("TENANT")}</div>
          <TenantSwitcher />
          {demo && <div className="mt-1 font-mono text-[10px] text-emerald-400">DEMO DATA</div>}
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5">
          {NAV.filter((n) => !n.superAdminOnly || can("*")).map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`nav-${n.id}-link`} onClick={() => setOpen(false)}
              className={({ isActive }) => cn("flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors", isActive ? "bg-blue-500/15 text-blue-300 border border-blue-500/20" : "text-slate-400 hover:text-slate-100 hover:bg-[#182030]")}>
              <n.icon className="h-4 w-4" /> {t(n.label)}
            </NavLink>
          ))}
        </nav>
        <div className="px-4 py-4 border-t border-[#26334d]">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-[#182030] border border-[#26334d] flex items-center justify-center font-display text-sm text-slate-200">{user.name?.[0]}</div>
            <div className="min-w-0 flex-1">
              <div data-testid="user-menu-name" className="text-sm text-slate-100 truncate">{user.name}</div>
              <div className="font-mono text-[10px] text-slate-500 uppercase">{user.effectiveRole}</div>
            </div>
            <Button variant="ghost" size="icon" data-testid="logout-button" onClick={async () => { await logout(); nav("/login"); }} className="text-slate-400 hover:text-rose-400"><LogOut className="h-4 w-4" /></Button>
          </div>
        </div>
      </aside>
      {open && <div className="fixed inset-0 bg-black/60 z-30 lg:hidden" onClick={() => setOpen(false)} />}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 h-16 tg-glass border-b border-[#26334d] flex items-center px-4 sm:px-6 gap-4">
          <button className="lg:hidden text-slate-300" onClick={() => setOpen(true)} data-testid="sidebar-open-button"><Menu className="h-5 w-5" /></button>
          <div className="flex items-center gap-2 text-emerald-400 text-xs font-mono"><Activity className="h-3.5 w-3.5 tg-pulse" /> LIVE</div>
          <Clock />
          <div className="ml-auto flex items-center gap-2">
            <LanguageSwitch compact />
            {can("uber:import") && <Button asChild size="sm" variant="outline" className="hidden sm:inline-flex border-[#26334d] bg-transparent text-slate-200 hover:bg-[#182030]" data-testid="header-import-uber-button"><Link to="/uber"><Upload className="h-4 w-4 mr-1" /> {t("Import Uber CSV")}</Link></Button>}
            {can("fleet:write") && <Button asChild size="sm" className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="header-add-vehicle-button"><Link to="/vehicles?wizard=1"><Car className="h-4 w-4 mr-1" /> {t("Add Vehicle")}</Link></Button>}
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-x-hidden"><Outlet /></main>
      </div>
    </div>
  );
}

export { BarChart3 };
